const API_BASE = '/api';

function getToken(): string | null {
  return localStorage.getItem('tekizai_token');
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'ネットワークエラー' }));
    throw new Error((error as { error: string }).error || `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// Auth
export const api = {
  auth: {
    register: (data: { company_name: string; email: string; password: string; name: string }) =>
      request<{ token: string; user: Record<string, unknown> }>('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
    login: (data: { email: string; password: string }) =>
      request<{ token: string; user: Record<string, unknown> }>('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
    me: () =>
      request<{ user: Record<string, unknown> }>('/auth/me'),
  },

  employees: {
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<{ employees: Record<string, unknown>[] }>(`/employees${qs}`);
    },
    get: (id: string) =>
      request<{ employee: Record<string, unknown> }>(`/employees/${id}`),
    create: (data: Record<string, unknown>) =>
      request<{ employee: Record<string, unknown> }>('/employees', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Record<string, unknown>) =>
      request<{ employee: Record<string, unknown> }>(`/employees/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) =>
      request<{ message: string }>(`/employees/${id}`, { method: 'DELETE' }),
    importCSV: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return request<{ total: number; created: number; updated: number; error_count: number; errors: Array<{ row: number; message: string }> }>(
        '/employees/import-csv',
        { method: 'POST', body: formData },
      );
    },
  },

  departments: {
    list: () =>
      request<{ departments: Record<string, unknown>[] }>('/departments'),
    create: (data: { name: string; parent_id?: string }) =>
      request<{ department: Record<string, unknown> }>('/departments', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Record<string, unknown>) =>
      request<{ department: Record<string, unknown> }>(`/departments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) =>
      request<{ message: string }>(`/departments/${id}`, { method: 'DELETE' }),
  },

  teams: {
    list: () =>
      request<{ teams: Record<string, unknown>[] }>('/teams'),
    get: (id: string) =>
      request<{ team: Record<string, unknown>; members: Record<string, unknown>[] }>(`/teams/${id}`),
    create: (data: { name: string; description?: string }) =>
      request<{ team: Record<string, unknown> }>('/teams', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Record<string, unknown>) =>
      request<{ team: Record<string, unknown> }>(`/teams/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) =>
      request<{ message: string }>(`/teams/${id}`, { method: 'DELETE' }),
    addMember: (teamId: string, data: { employee_id: string; role_in_team?: string }) =>
      request<{ message: string }>(`/teams/${teamId}/members`, { method: 'POST', body: JSON.stringify(data) }),
    removeMember: (teamId: string, employeeId: string) =>
      request<{ message: string }>(`/teams/${teamId}/members/${employeeId}`, { method: 'DELETE' }),
  },

  ai: {
    recommendWorkstyle: (employeeId: string, forceRefresh = false) =>
      request<{ recommendation: Record<string, unknown>; cached: boolean; tokens_used: number; analyzed_at?: string }>(
        '/ai/recommend-workstyle', { method: 'POST', body: JSON.stringify({ employee_id: employeeId, force_refresh: forceRefresh }) },
      ),
    analyzeTeam: (teamId: string, forceRefresh = false) =>
      request<{ recommendation: Record<string, unknown>; cached: boolean; tokens_used: number }>(
        '/ai/analyze-team', { method: 'POST', body: JSON.stringify({ team_id: teamId, force_refresh: forceRefresh }) },
      ),
    analyzeCompatibility: (data: { employee_a_id: string; employee_b_id: string; relationship: string }) =>
      request<{ recommendation: Record<string, unknown>; cached: boolean; tokens_used: number }>(
        '/ai/analyze-compatibility', { method: 'POST', body: JSON.stringify(data) },
      ),
    burnoutAlerts: (forceRefresh = false) =>
      request<Record<string, unknown>>('/ai/burnout-alerts', { method: 'POST', body: JSON.stringify({ force_refresh: forceRefresh }) }),
    managerTool: (data: { tool_type: string; target_type: string; employee_id?: string; team_id?: string; context?: string; force_refresh?: boolean }) =>
      request<{ result: Record<string, unknown>; cached: boolean; tokens_used: number }>(
        '/ai/manager-tools', { method: 'POST', body: JSON.stringify(data) },
      ),
  },

  reports: {
    organization: () =>
      request<Record<string, unknown>>('/reports/organization', { method: 'POST' }),
    individual: (employeeId: string) =>
      request<Record<string, unknown>>(`/reports/individual/${employeeId}`, { method: 'POST' }),
  },
};
